// --- src/data/m3/Unit10.js ---

export const M3_UNIT_10 = {
  id: "m3-u10-favorites-consolidation",
  title: "Unit 10: Favorites & Preferences",
  topic: "Express preferences using favorite, like, and like more. Recycle vocabulary from all modules.",
  iconType: "star",
  grammarImage: "/m3-u10-favorites.jpg",
  grammarText: "Consolida preferencias aplicando vocabulario de animales, familia, países, casa, deportes y música. Usa 'like more' sin comparativos formales. Recicla todo lo aprendido en contextos conversacionales reales.",
  sets: [

    // ===================================================================
    // SET 1: FAVORITE + TYPE OF (Animals, Family, Places) - A2
    // ===================================================================
    {
      id: "m3-u10-set1",
      title: "Set 1: Favorite Things ⭐",
      context: "Sábado por la mañana - Casa de Alex. Alex está haciendo una lista de sus cosas favoritas para un proyecto escolar. 'Mi animal favorito es el perro', escribe. 'Mi país favorito es Italia... mi habitación favorita es la cocina...'. — Saturday morning - Alex's house. Alex is making a list of his favorite things for a school project. 'My favorite animal is the dog,' he writes. 'My favorite country is Italy... my favorite room is the kitchen...'.",
      questions: [
        {
          type: "match",
          q: "Match 'favorite' structure to category.",
          pairs: [
            { left: "My favorite animal is...", right: "Dogs / cats / parrots" },
            { left: "My favorite country is...", right: "Spain / Japan / Mexico" },
            { left: "My favorite room is...", right: "Kitchen / bedroom / living room" }
          ],
          a: "completed",
          tip: "Favorite + noun! Use with ANY category!",
          successtip: "Favorite structure! Any topic!"
        },
        {
          type: "mc",
          q: "Which is correct?",
          options: ["My favorite pet is my dog", "My favorite pet is dog", "My pet favorite is my dog"],
          a: "My favorite pet is my dog",
          tip: "My favorite + CATEGORY + is + SPECIFIC!",
          successtip: "My dog NOT just dog!"
        },
        {
          type: "input",
          q: "Complete: 'My favorite ___ is Italy because it's beautiful.' (category)",
          a: "country",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Category = country! Italy is a country!",
          successtip: "Country! Category word!"
        },
        {
          type: "order",
          q: "Traduce: 'Mi animal favorito es el caballo.'",
          words: ["My", "favorite", "animal", "is", "the", "horse."],
          a: "My favorite animal is the horse.",
          tip: "My favorite + animal + is + the horse!",
          successtip: "Perfect structure!"
        },
        {
          type: "mc",
          q: "My favorite type of ___ is rock music.",
          options: ["music", "song", "musician"],
          a: "music",
          tip: "'Type of' needs GENERAL category!",
          successtip: "Music, not song!"
        },
        {
          type: "input",
          q: "Complete: 'My favorite place in my house is the ___ because it's quiet.' (room)",
          a: "bedroom",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["living room", "garden"] },
          tip: "Any room! Bedroom, living room, garden!",
          successtip: "House room! M2 vocab!"
        },
        {
          type: "match",
          q: "Match favorite to reason (because).",
          pairs: [
            { left: "My favorite animal is dogs", right: "because they're friendly" },
            { left: "My favorite country is Japan", right: "because the food is delicious" },
            { left: "My favorite sport is tennis", right: "because it's fun" }
          ],
          a: "completed",
          tip: "Favorite + because + reason!",
          successtip: "Reasons linked!"
        },
        {
          type: "order",
          q: "Rewrite: 'I love cats' → Using 'favorite'",
          words: ["Cats", "are", "my", "favorite", "animal."],
          a: "Cats are my favorite animal.",
          tip: "'I love' = 'are my favorite'!",
          successtip: "Structure converted!"
        },
        {
          type: "mc",
          q: "Which uses 'type of' correctly?",
          options: ["My favorite type of food is Italian food", "My favorite type of Italy is food", "My type favorite of food is Italian"],
          a: "My favorite type of food is Italian food",
          tip: "'Type of' + CATEGORY + is + SPECIFIC!",
          successtip: "Type of food is Italian!"
        },
        {
          type: "input",
          q: "Complete: 'My favorite ___ is my grandmother because she's very kind.' (family member category)",
          a: "relative",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["family member", "person", "grandparent"] },
          tip: "Relative, family member, person all work!",
          successtip: "Family category! M1 vocab!"
        }
      ]
    },

    // ===================================================================
    // SET 2: LIKE MORE (Simple Preference) - A2
    // ===================================================================
    {
      id: "m3-u10-set2",
      title: "Set 2: I Like... More 🔄",
      context: "Domingo por la tarde - Parque. Alex camina con su amiga Emma. 'Me gustan los gatos, pero me gustan más los perros', dice Alex. Emma pregunta '¿Por qué?' Alex responde 'Porque los perros son más activos'. — Sunday afternoon - Park. Alex walks with his friend Emma. 'I like cats, but I like dogs more,' says Alex. Emma asks 'Why?' Alex answers 'Because dogs are more active.'.",
      questions: [
        {
          type: "match",
          q: "Match structure to meaning.",
          pairs: [
            { left: "I like dogs", right: "General preference (me gustan)" },
            { left: "I like dogs more", right: "Stronger preference (me gustan más)" },
            { left: "Dogs are my favorite", right: "Top choice (los favoritos)" }
          ],
          a: "completed",
          tip: "Three levels! Like, like more, favorite!",
          successtip: "Preference levels clear!"
        },
        {
          type: "mc",
          q: "I like singing, but I like dancing more because it's ___.",
          options: ["fun", "boring", "difficult"],
          a: "fun",
          tip: "'Like more' = prefer! Because + positive adjective!",
          successtip: "Fun! Dancing is fun!"
        },
        {
          type: "input",
          q: "Complete: 'I like cats, but I like dogs ___ because they're more friendly.'",
          a: "more",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Like + MORE! Simple preference!",
          successtip: "More! Simple structure!"
        },
        {
          type: "order",
          q: "Traduce: 'Me gusta el fútbol, pero me gusta más el tenis.'",
          words: ["I", "like", "soccer,", "but", "I", "like", "tennis", "more."],
          a: "I like soccer, but I like tennis more.",
          tip: "'Más' = more! But = contrast!",
          successtip: "Like more! Contrast!"
        },
        {
          type: "mc",
          q: "Which is correct?",
          options: ["I like pizza, but I like pasta more", "I like more pasta than pizza", "I more like pasta"],
          a: "I like pizza, but I like pasta more",
          tip: "Simple! No 'than'! Just 'like X, but like Y more'!",
          successtip: "No 'than' needed!"
        },
        {
          type: "input",
          q: "Complete: 'I like my ___, but I like my ___ more.' (kitchen, bedroom)",
          a: "kitchen bedroom",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["bedroom kitchen", "living room bedroom"] },
          tip: "Any two rooms! Preference between two!",
          successtip: "Two rooms! M2 vocab!"
        },
        {
          type: "match",
          q: "Match preference to reason.",
          pairs: [
            { left: "I like rock, but I like jazz more", right: "because it's relaxing" },
            { left: "I like swimming, but I like tennis more", right: "because it's exciting" },
            { left: "I like Mexico, but I like Spain more", right: "because the food is amazing" }
          ],
          a: "completed",
          tip: "Like more + because + reason!",
          successtip: "All modules vocab!"
        },
        {
          type: "order",
          q: "Complete with reasons: 'I like my cat, but ___'",
          words: ["I", "like", "my", "dog", "more", "because", "he's", "playful."],
          a: "I like my dog more because he's playful.",
          tip: "Like more + because + adjective!",
          successtip: "Complete structure!"
        },
        {
          type: "mc",
          q: "I like all sports, but I like ___ more because it's fast.",
          options: ["basketball", "basketballs", "a basketball"],
          a: "basketball",
          tip: "Sport in general = no article!",
          successtip: "Basketball! No article!"
        },
        {
          type: "input",
          q: "Complete: 'I like classical music, but I like ___ music more because it's energetic.'",
          a: "rock",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["pop", "hip-hop", "electronic"] },
          tip: "Any energetic genre! Rock, pop, hip-hop!",
          successtip: "Music genre! M3 vocab!"
        }
      ]
    },

    // ===================================================================
    // SET 3: MINI-READING - My Favorite Pet & Why 📖
    // ===================================================================
    {
      id: "m3-u10-set3",
      title: "Set 3: Reading - My Pets 📖",
      context: "Lunes en la escuela - Clase de inglés. La maestra pide a los estudiantes leer un texto sobre mascotas y responder preguntas. — Monday at school - English class. The teacher asks students to read a text about pets and answer questions.",
      questions: [
        {
          type: "mc",
          passage: "I have three pets: a dog, a cat, and a parrot. My favorite pet is my dog because he's very friendly and playful. I like my cat, but I like my dog more because dogs are more active than cats. I don't like my parrot very much because he's very noisy in the morning.",
          q: "What is his favorite pet?",
          options: ["Dog", "Cat", "Parrot"],
          a: "Dog",
          tip: "Check the second sentence!",
          successtip: "Dog! His favorite!"
        },
        {
          type: "mc",
          q: "Why is the dog his favorite?",
          options: ["Friendly and playful", "Quiet", "Small"],
          a: "Friendly and playful",
          tip: "Two reasons in the text!",
          successtip: "Friendly and playful!"
        },
        {
          type: "input",
          q: "Dogs are more ___ than cats according to the text.",
          a: "active",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'Dogs are more active than cats'!",
          successtip: "Active! Detail found!"
        },
        {
          type: "order",
          q: "Why doesn't he like the parrot much? Complete:",
          words: ["because", "he's", "very", "noisy", "in", "the", "morning"],
          a: "because he's very noisy in the morning",
          tip: "Reason for dislike! Because + negative quality!",
          successtip: "Noisy in the morning!"
        },
        {
          type: "mc",
          q: "Which structure does he use for his dog?",
          options: ["My favorite pet is my dog", "I like dog", "Dog is favorite"],
          a: "My favorite pet is my dog",
          tip: "'My favorite pet is...' structure!",
          successtip: "Favorite structure identified!"
        },
        {
          type: "input",
          q: "He uses 'I like my cat, but I like my dog ___' to show preference.",
          a: "more",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'Like more' structure!",
          successtip: "More! Preference found!"
        },
        {
          type: "mc",
          q: "How many preference structures does he use?",
          options: ["Three: favorite, like more, don't like much", "One: favorite", "Two: like and love"],
          a: "Three: favorite, like more, don't like much",
          tip: "Count different structures!",
          successtip: "Three structures!"
        },
        {
          type: "input",
          q: "Complete the negative: 'I don't like my parrot very ___.'",
          a: "much",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'Don't like very much' = mild dislike!",
          successtip: "Much! Negative phrase!"
        },
        {
          type: "order",
          q: "Rewrite his cat preference using 'favorite': 'My ___ favorite pet is my cat.'",
          words: ["My", "second", "favorite", "pet", "is", "my", "cat."],
          a: "My second favorite pet is my cat.",
          tip: "'Second favorite' = not #1 but still liked!",
          successtip: "Second favorite! Ranking!"
        },
        {
          type: "mc",
          q: "What time is the parrot noisy?",
          options: ["In the morning", "At night", "All day"],
          a: "In the morning",
          tip: "'Very noisy in the morning'!",
          successtip: "In the morning! Time detail!"
        }
      ]
    },

    // ===================================================================
    // SET 4: FAVORITE PLACES (Town, House, Countries) - A2
    // ===================================================================
    {
      id: "m3-u10-set4",
      title: "Set 4: Favorite Places 🌍",
      context: "Martes por la tarde - Proyecto de geografía. Emma escribe sobre lugares. 'Mi país favorito es Japón porque la comida es deliciosa. Mi lugar favorito en mi ciudad es el parque porque es tranquilo.' — Tuesday afternoon - Geography project. Emma writes about places. 'My favorite country is Japan because the food is delicious. My favorite place in my town is the park because it's quiet.'.",
      questions: [
        {
          type: "match",
          q: "Match place type to example.",
          pairs: [
            { left: "Favorite country", right: "Japan / Spain / Brazil" },
            { left: "Favorite place in town", right: "Park / library / cinema" },
            { left: "Favorite room in house", right: "Kitchen / bedroom / garden" }
          ],
          a: "completed",
          tip: "Three place categories!",
          successtip: "Countries, town, house!"
        },
        {
          type: "mc",
          q: "My favorite country is ___ because the food is delicious and the people are friendly.",
          options: ["Italy", "Italian", "Italians"],
          a: "Italy",
          tip: "Country (noun) NOT nationality!",
          successtip: "Italy! Country noun!"
        },
        {
          type: "input",
          q: "Complete: 'My favorite ___ in my town is the park because it's beautiful.' (category)",
          a: "place",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Place = category word!",
          successtip: "Place! Town category!"
        },
        {
          type: "order",
          q: "Traduce: 'Mi lugar favorito en mi casa es la cocina.'",
          words: ["My", "favorite", "place", "in", "my", "house", "is", "the", "kitchen."],
          a: "My favorite place in my house is the kitchen.",
          tip: "Place IN house! Preposition 'in'!",
          successtip: "House place! Complete!"
        },
        {
          type: "mc",
          q: "I like the library, but I like the ___ more because it's quieter.",
          options: ["park", "parks", "a park"],
          a: "park",
          tip: "Specific place in your town = 'the park'!",
          successtip: "The park! Like more!"
        },
        {
          type: "input",
          q: "Complete: 'My favorite room is the ___, but I like the ___ more because it's sunny.' (bedroom, kitchen)",
          a: "bedroom kitchen",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["kitchen bedroom", "living room garden"] },
          tip: "Two rooms! Favorite vs like more!",
          successtip: "Two rooms! Contrast!"
        },
        {
          type: "match",
          q: "Match place to reason.",
          pairs: [
            { left: "My favorite country is Japan", right: "because the culture is interesting" },
            { left: "My favorite place is the cinema", right: "because I love movies" },
            { left: "My favorite room is the garden", right: "because it's relaxing" }
          ],
          a: "completed",
          tip: "All types of places with reasons!",
          successtip: "Places + reasons!"
        },
        {
          type: "order",
          q: "Complete negative: 'I don't like ___ very much because ___'",
          words: ["I", "don't", "like", "the", "city", "center", "because", "it's", "noisy."],
          a: "I don't like the city center because it's noisy.",
          tip: "Negative place preference!",
          successtip: "Negative + reason!"
        },
        {
          type: "mc",
          q: "Which is correct?",
          options: ["My favorite place in town is the museum", "My favorite in town place is the museum", "My place favorite is the museum"],
          a: "My favorite place in town is the museum",
          tip: "Word order! My favorite + PLACE + in town!",
          successtip: "Correct word order!"
        },
        {
          type: "input",
          q: "Complete: 'My favorite type of ___ is big cities because they're exciting.' (place category)",
          a: "place",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["city", "town"] },
          tip: "'Type of place' OR 'type of city'!",
          successtip: "Place type! Category!"
        }
      ]
    },

    // ===================================================================
    // SET 5: FAMILY PREFERENCES (Who You Like/Don't Like + Because) - A2/B1-
    // ===================================================================
    {
      id: "m3-u10-set5",
      title: "Set 5: Family & People 👨‍👩‍👦",
      context: "Miércoles en casa - Conversación familiar. Alex habla con su mamá sobre la familia. 'Mi familiar favorito eres tú, mamá, porque eres muy amable. Me cae bien mi tía, pero me caes mejor tú. No me cae muy bien mi primo porque es aburrido.' — Wednesday at home - Family conversation. Alex talks with his mom about family. 'My favorite relative is you, mom, because you're very kind. I like my aunt, but I like you more. I don't like my cousin very much because he's boring.'.",
      questions: [
        {
          type: "match",
          q: "Match family preference to structure.",
          pairs: [
            { left: "My favorite relative is my aunt", right: "Top choice" },
            { left: "I like my uncle, but I like my aunt more", right: "Preference between two" },
            { left: "I don't like my cousin very much", right: "Mild dislike" }
          ],
          a: "completed",
          tip: "Three ways to talk about family!",
          successtip: "Family preferences!"
        },
        {
          type: "mc",
          q: "I don't like my ___ very much because he's boring.",
          options: ["cousin", "cousins", "a cousin"],
          a: "cousin",
          tip: "Specific relative = 'my cousin' (singular)!",
          successtip: "My cousin! Singular!"
        },
        {
          type: "input",
          q: "Complete: 'My favorite ___ is my grandmother because she's very kind.' (family category)",
          a: "relative",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["family member", "person", "grandparent"] },
          tip: "Relative, family member, grandparent!",
          successtip: "Family category!"
        },
        {
          type: "order",
          q: "Traduce: 'No me cae bien mi hermano porque es muy ruidoso.'",
          words: ["I", "don't", "like", "my", "brother", "because", "he's", "very", "noisy."],
          a: "I don't like my brother because he's very noisy.",
          tip: "'No me cae bien' = don't like!",
          successtip: "Family dislike + reason!"
        },
        {
          type: "mc",
          q: "I like my sister, but I like my ___ more because she's more fun.",
          options: ["cousin", "cousins", "the cousin"],
          a: "cousin",
          tip: "One specific cousin = 'my cousin'!",
          successtip: "My cousin! Like more!"
        },
        {
          type: "input",
          q: "Complete: 'My favorite person in my family is my ___ because he's very funny.'",
          a: "father",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["dad", "brother", "uncle", "grandfather"] },
          tip: "Any male relative! Father, dad, brother, uncle!",
          successtip: "Male relative! Funny!"
        },
        {
          type: "match",
          q: "Match family member to reason.",
          pairs: [
            { left: "I like my aunt", right: "because she's generous" },
            { left: "I don't like my uncle", right: "because he's too serious" },
            { left: "My favorite is my grandma", right: "because she's warm" }
          ],
          a: "completed",
          tip: "Family + reasons! Positive and negative!",
          successtip: "Family + personality!"
        },
        {
          type: "order",
          q: "Complete preference: 'I like all my cousins, but ___'",
          words: ["I", "like", "Maria", "more", "because", "she's", "friendly."],
          a: "I like Maria more because she's friendly.",
          tip: "Specific name! Like more + because!",
          successtip: "Named person! Specific!"
        },
        {
          type: "mc",
          q: "Which is correct to say 'no me cae muy bien'?",
          options: ["I don't like him very much", "I don't like very much him", "I very much don't like him"],
          a: "I don't like him very much",
          tip: "Word order! Don't like + person + very much!",
          successtip: "Correct word order!"
        },
        {
          type: "input",
          q: "Complete: 'My least favorite relative is my ___ because he's very ___.' (cousin, annoying)",
          a: "cousin annoying",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["brother annoying", "uncle boring"] },
          tip: "Least favorite = bottom choice!",
          successtip: "Least favorite! Negative!"
        }
      ]
    },

    // ===================================================================
    // SET 6: MINI-READING - Countries I Love 📖
    // ===================================================================
    {
      id: "m3-u10-set6",
      title: "Set 6: Reading - My Favorite Countries 📖",
      context: "Jueves - Proyecto de viajes. Los estudiantes leen un texto sobre países favoritos y opiniones sobre viajes. — Thursday - Travel project. Students read a text about favorite countries and travel opinions.",
      questions: [
        {
          type: "mc",
          passage: "I love traveling. My favorite country is Japan because the culture is fascinating and the food is amazing. I like Italy, but I like Japan more because Japanese cities are more modern and clean. I also like Spain because the people are very friendly, but I don't like Mexico very much because it's too hot for me. I want to visit Brazil next year because I love beaches.",
          q: "What is his favorite country?",
          options: ["Japan", "Italy", "Spain"],
          a: "Japan",
          tip: "Check the second sentence!",
          successtip: "Japan! His favorite!"
        },
        {
          type: "mc",
          q: "Why is Japan his favorite?",
          options: ["Culture and food", "Weather", "Language"],
          a: "Culture and food",
          tip: "Two reasons in the text!",
          successtip: "Culture and food!"
        },
        {
          type: "input",
          q: "He likes Italy, but he likes Japan ___ because Japanese cities are more modern.",
          a: "more",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'Like more' structure!",
          successtip: "More! Preference!"
        },
        {
          type: "order",
          q: "Why does he like Spain? Complete:",
          words: ["because", "the", "people", "are", "very", "friendly"],
          a: "because the people are very friendly",
          tip: "Reason for Spain! People + adjective!",
          successtip: "People are friendly!"
        },
        {
          type: "mc",
          q: "Why doesn't he like Mexico very much?",
          options: ["It's too hot", "It's too cold", "It's too expensive"],
          a: "It's too hot",
          tip: "'Too hot for me' = personal reason!",
          successtip: "Too hot! Personal!"
        },
        {
          type: "input",
          q: "Which country does he want to visit? ___",
          a: "Brazil",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'I want to visit Brazil next year'!",
          successtip: "Brazil! Future plan!"
        },
        {
          type: "mc",
          q: "Why does he want to visit Brazil?",
          options: ["He loves beaches", "He likes the food", "He speaks Portuguese"],
          a: "He loves beaches",
          tip: "'Because I love beaches'!",
          successtip: "Loves beaches!"
        },
        {
          type: "input",
          q: "How many preference structures does he use? (favorite, like more, don't like much, want to visit)",
          a: "four",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["4"] },
          tip: "Four structures! Count them!",
          successtip: "Four! All identified!"
        },
        {
          type: "order",
          q: "Rewrite using 'least favorite': 'My ___ favorite country is Mexico.'",
          words: ["My", "least", "favorite", "country", "is", "Mexico."],
          a: "My least favorite country is Mexico.",
          tip: "'Least favorite' = bottom choice!",
          successtip: "Least favorite! Negative!"
        },
        {
          type: "mc",
          q: "Which adjective does he use for Japanese cities?",
          options: ["Modern and clean", "Old and dirty", "Big and noisy"],
          a: "Modern and clean",
          tip: "'More modern and clean'!",
          successtip: "Modern and clean!"
        }
      ]
    },

    // ===================================================================
    // SET 7: COMBINING PREFERENCES (Sports + Music + Food) - A2/B1-
    // ===================================================================
    {
      id: "m3-u10-set7",
      title: "Set 7: Combining Topics 🎯",
      context: "Viernes - Encuesta escolar. Los estudiantes completan un cuestionario sobre sus preferencias en deportes, música y comida. Alex escribe: 'Mi deporte favorito es el tenis, mi música favorita es el rock, y mi comida favorita es la pizza.' — Friday - School survey. Students complete a questionnaire about their preferences in sports, music, and food. Alex writes: 'My favorite sport is tennis, my favorite music is rock, and my favorite food is pizza.'.",
      questions: [
        {
          type: "match",
          q: "Match combined preferences.",
          pairs: [
            { left: "Favorite sport + music", right: "Tennis and rock music" },
            { left: "Favorite food + drink", right: "Pizza and orange juice" },
            { left: "Favorite room + place in town", right: "Kitchen and library" }
          ],
          a: "completed",
          tip: "Combining categories! Using 'and'!",
          successtip: "Multiple preferences!"
        },
        {
          type: "mc",
          q: "My favorite sport is tennis, my favorite music is rock, and my favorite food is pizza. How many preferences?",
          options: ["Three", "Two", "One"],
          a: "Three",
          tip: "Three different categories!",
          successtip: "Three! Sport, music, food!"
        },
        {
          type: "input",
          q: "Complete: 'My favorite ___ is tennis, my favorite ___ is rock, and my favorite ___ is pizza.' (categories)",
          a: "sport music food",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Three categories! Sport, music, food!",
          successtip: "Three categories! All correct!"
        },
        {
          type: "order",
          q: "Combine using 'and': Sport=swimming, Music=jazz, Food=pasta →",
          words: ["My", "favorites", "are", "swimming,", "jazz,", "and", "pasta."],
          a: "My favorites are swimming, jazz, and pasta.",
          tip: "Plural 'favorites'! Three items = commas + and!",
          successtip: "Combined with commas!"
        },
        {
          type: "mc",
          q: "I like soccer and basketball, but I like ___ more because it's faster.",
          options: ["basketball", "basketballs", "the basketball"],
          a: "basketball",
          tip: "Sport in general! No article!",
          successtip: "Basketball! No article!"
        },
        {
          type: "input",
          q: "Complete: 'I like rock and pop, but I like ___ more because it's energetic.' (rock or pop)",
          a: "rock",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["pop"] },
          tip: "Either answer! Personal preference!",
          successtip: "Music preference! Either!"
        },
        {
          type: "match",
          q: "Match topic combinations to examples.",
          pairs: [
            { left: "Sport + reason", right: "Tennis because it's fun" },
            { left: "Music + reason", right: "Jazz because it's relaxing" },
            { left: "Food + reason", right: "Pizza because it's delicious" }
          ],
          a: "completed",
          tip: "All topics + reasons!",
          successtip: "Topics + reasons!"
        },
        {
          type: "order",
          q: "Traduce: 'Mi deporte favorito es nadar y mi música favorita es el jazz.'",
          words: ["My", "favorite", "sport", "is", "swimming", "and", "my", "favorite", "music", "is", "jazz."],
          a: "My favorite sport is swimming and my favorite music is jazz.",
          tip: "Two preferences connected with 'and'!",
          successtip: "Two favorites combined!"
        },
        {
          type: "mc",
          q: "Which correctly combines three preferences?",
          options: ["I like soccer, swimming, and tennis", "I like soccer and swimming and tennis", "I like soccer, and swimming, and tennis"],
          a: "I like soccer, swimming, and tennis",
          tip: "List format! Commas between items, 'and' before last!",
          successtip: "Correct list format!"
        },
        {
          type: "input",
          q: "Complete contrast: 'My favorite sport is tennis, but my favorite ___ is swimming.' (activity contrast)",
          a: "hobby",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["pastime", "activity", "exercise"] },
          tip: "Different category! Sport vs hobby!",
          successtip: "Category contrast!"
        }
      ]
    },

    // ===================================================================
    // SET 8: NEGATIVE PREFERENCES (Don't Like, Least Favorite) - A2/B1-
    // ===================================================================
    {
      id: "m3-u10-set8",
      title: "Set 8: What I Don't Like 🚫",
      context: "Sábado por la tarde - Conversación con Emma. Alex y Emma hablan sobre cosas que no les gustan. 'No me gustan las arañas porque dan miedo', dice Alex. 'A mí tampoco. Mi animal menos favorito son las serpientes', responde Emma. — Saturday afternoon - Conversation with Emma. Alex and Emma talk about things they don't like. 'I don't like spiders because they're scary,' says Alex. 'Me neither. My least favorite animal is snakes,' Emma responds.",
      questions: [
        {
          type: "match",
          q: "Match negative structures.",
          pairs: [
            { left: "I don't like snakes", right: "General dislike" },
            { left: "I don't like snakes very much", right: "Mild dislike" },
            { left: "Snakes are my least favorite", right: "Bottom choice" }
          ],
          a: "completed",
          tip: "Three negative levels!",
          successtip: "Negative scales!"
        },
        {
          type: "mc",
          q: "I don't like ___ because they're scary.",
          options: ["snakes", "snake", "a snake"],
          a: "snakes",
          tip: "General dislike = plural!",
          successtip: "Snakes! Plural!"
        },
        {
          type: "input",
          q: "Complete: 'I don't like my ___ very much because it's too small.' (room)",
          a: "bedroom",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["kitchen", "bathroom", "living room"] },
          tip: "Any room! Don't like much = mild!",
          successtip: "Room dislike! Mild!"
        },
        {
          type: "order",
          q: "Traduce: 'No me gusta mucho el fútbol porque es aburrido.'",
          words: ["I", "don't", "like", "soccer", "very", "much", "because", "it's", "boring."],
          a: "I don't like soccer very much because it's boring.",
          tip: "'No mucho' = don't like very much!",
          successtip: "Mild dislike + reason!"
        },
        {
          type: "mc",
          q: "My least favorite ___ is classical music because it's too slow.",
          options: ["type of music", "music type", "musics"],
          a: "type of music",
          tip: "'Type of music' = category!",
          successtip: "Type of music! Category!"
        },
        {
          type: "input",
          q: "Complete: 'I love dogs and cats, but I don't like ___ because they're noisy.' (parrots)",
          a: "parrots",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Contrast: love some, don't like others!",
          successtip: "Parrots! Contrast!"
        },
        {
          type: "match",
          q: "Match negative to reason.",
          pairs: [
            { left: "I don't like my cousin", right: "because he's annoying" },
            { left: "I don't like Mexico much", right: "because it's too hot" },
            { left: "My least favorite sport is golf", right: "because it's boring" }
          ],
          a: "completed",
          tip: "Negatives with reasons!",
          successtip: "Negatives + reasons!"
        },
        {
          type: "order",
          q: "Complete: 'I like all animals, but ___'",
          words: ["I", "don't", "like", "spiders", "because", "they're", "scary."],
          a: "I don't like spiders because they're scary.",
          tip: "Contrast! Like all BUT don't like one!",
          successtip: "Exception expressed!"
        },
        {
          type: "mc",
          q: "Which is the strongest negative?",
          options: ["I hate rock music", "I don't like rock music very much", "Rock music is my least favorite"],
          a: "I hate rock music",
          tip: "Hate = strongest!",
          successtip: "Hate! Strongest negative!"
        },
        {
          type: "input",
          q: "Complete: 'My least favorite ___ in my town is the bus station because it's dirty.' (place)",
          a: "place",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Place = category!",
          successtip: "Place! Negative preference!"
        }
      ]
    },

    // ===================================================================
    // SET 9: MINI-READING - What I Don't Like & Why 📖
    // ===================================================================
    {
      id: "m3-u10-set9",
      title: "Set 9: Reading - My Dislikes 📖",
      context: "Domingo - Tarea de escritura. Los estudiantes leen un texto sobre cosas que no les gustan a alguien y por qué. — Sunday - Writing homework. Students read a text about things someone doesn't like and why.",
      questions: [
        {
          type: "mc",
          passage: "There are many things I don't like. My least favorite animal is spiders because they're very scary. I don't like my city center very much because it's too noisy and dirty. I also don't like classical music because it's boring for me. My least favorite family member is my uncle Tom because he's too serious and never smiles. I like most sports, but I don't like golf very much because it's too slow.",
          q: "What is his least favorite animal?",
          options: ["Spiders", "Snakes", "Rats"],
          a: "Spiders",
          tip: "Check the second sentence!",
          successtip: "Spiders! Least favorite!"
        },
        {
          type: "mc",
          q: "Why is his least favorite animal spiders?",
          options: ["They're scary", "They're big", "They're fast"],
          a: "They're scary",
          tip: "'Because they're very scary'!",
          successtip: "Scary! Reason found!"
        },
        {
          type: "input",
          q: "He doesn't like the city center because it's too ___ and ___. (two reasons)",
          a: "noisy dirty",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["dirty noisy"] },
          tip: "Two negative adjectives!",
          successtip: "Noisy and dirty! Both!"
        },
        {
          type: "order",
          q: "Why doesn't he like classical music? Complete:",
          words: ["because", "it's", "boring", "for", "me"],
          a: "because it's boring for me",
          tip: "'For me' = personal opinion!",
          successtip: "Boring for me! Personal!"
        },
        {
          type: "mc",
          q: "What's wrong with uncle Tom?",
          options: ["Too serious and never smiles", "Too noisy", "Too friendly"],
          a: "Too serious and never smiles",
          tip: "Two problems!",
          successtip: "Two personality issues!"
        },
        {
          type: "input",
          q: "He likes most sports, but he doesn't like ___ very much because it's too slow.",
          a: "golf",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Exception: likes most BUT not this one!",
          successtip: "Golf! Sport exception!"
        },
        {
          type: "mc",
          q: "How many times does he use 'least favorite'?",
          options: ["Two times (animal and family member)", "One time", "Three times"],
          a: "Two times (animal and family member)",
          tip: "Count 'least favorite'!",
          successtip: "Two times! Counted!"
        },
        {
          type: "input",
          q: "He uses 'don't like very much' to express ___ dislike. (mild/strong)",
          a: "mild",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'Very much' makes it MILD!",
          successtip: "Mild! Intensity identified!"
        },
        {
          type: "order",
          q: "Rewrite using 'hate': 'My least favorite animal is spiders' →",
          words: ["I", "hate", "spiders."],
          a: "I hate spiders.",
          tip: "'Least favorite' = hate!",
          successtip: "Hate! Strongest negative!"
        },
        {
          type: "mc",
          q: "Which structure does he use most?",
          options: ["Don't like (very much)", "Hate", "Can't stand"],
          a: "Don't like (very much)",
          tip: "'Don't like' appears most!",
          successtip: "Don't like! Most frequent!"
        }
      ]
    },

    // ===================================================================
    // SET 10: CONVERSATIONAL PRACTICE (Questions + Answers) - B1-
    // ===================================================================
    {
      id: "m3-u10-set10",
      title: "Set 10: Conversation Practice 💬",
      context: "Lunes en la escuela - Práctica de conversación. Los estudiantes practican hacer y responder preguntas sobre preferencias. Alex pregunta a Emma: '¿Cuál es tu animal favorito?' Emma responde: 'Mi animal favorito son los perros.' — Monday at school - Conversation practice. Students practice asking and answering questions about preferences. Alex asks Emma: 'What's your favorite animal?' Emma answers: 'My favorite animal is dogs.'.",
      questions: [
        {
          type: "match",
          q: "Match question to answer structure.",
          pairs: [
            { left: "What's your favorite animal?", right: "My favorite animal is dogs" },
            { left: "Which do you like more, cats or dogs?", right: "I like dogs more" },
            { left: "Why don't you like snakes?", right: "Because they're scary" }
          ],
          a: "completed",
          tip: "Three question types!",
          successtip: "Q&A patterns! Conversation!"
        },
        {
          type: "mc",
          q: "A: 'What's your favorite country?' B: '___'",
          options: ["My favorite country is Japan", "Japan is favorite", "I like country Japan"],
          a: "My favorite country is Japan",
          tip: "Answer mirrors question!",
          successtip: "Complete answer!"
        },
        {
          type: "input",
          q: "Question: 'Which do you like more, swimming or tennis?' Answer: 'I like ___ more.'",
          a: "swimming",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["tennis"] },
          tip: "Choose one! Personal preference!",
          successtip: "Preference expressed!"
        },
        {
          type: "order",
          q: "Form question: 'favorite / what / your / is / sport'",
          words: ["What's", "your", "favorite", "sport?"],
          a: "What's your favorite sport?",
          tip: "What's = What is! Question about favorite!",
          successtip: "Question formed!"
        },
        {
          type: "mc",
          q: "A: 'Why is your favorite room the kitchen?' B: 'Because ___.'",
          options: ["it's warm and cozy", "the kitchen", "I like rooms"],
          a: "it's warm and cozy",
          tip: "Answer 'why' with 'because + reason'!",
          successtip: "Reason given!"
        },
        {
          type: "input",
          q: "A: 'What's your least favorite animal?' B: 'My least favorite ___ is ___.' (animal, spiders)",
          a: "animal spiders",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["animal snakes"] },
          tip: "Echo the category! Animal!",
          successtip: "Category + specific!"
        },
        {
          type: "match",
          q: "Match conversation to topic.",
          pairs: [
            { left: "A: What's your favorite pet? B: Dogs", right: "Animals (M1)" },
            { left: "A: Which country do you like more? B: Spain", right: "Countries (M1)" },
            { left: "A: What's your favorite sport? B: Tennis", right: "Sports (M3)" }
          ],
          a: "completed",
          tip: "All modules topics!",
          successtip: "All topics covered!"
        },
        {
          type: "order",
          q: "Answer: 'Why do you like your grandmother more than your uncle?' → Because ___",
          words: ["Because", "she's", "kinder", "and", "more", "fun."],
          a: "Because she's kinder and more fun.",
          tip: "Because + two reasons!",
          successtip: "Two reasons! Complete!"
        },
        {
          type: "mc",
          q: "Natural response to: 'Do you like rock or jazz more?' →",
          options: ["I like jazz more because it's relaxing", "Yes, I like", "Rock and jazz"],
          a: "I like jazz more because it's relaxing",
          tip: "Choose one + reason!",
          successtip: "Natural conversation!"
        },
        {
          type: "input",
          q: "Complete dialogue: A: 'What's your favorite place in your house?' B: 'My favorite ___ is the ___.' (place, garden)",
          a: "place garden",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["place kitchen", "place bedroom"] },
          tip: "Echo category + specific!",
          successtip: "Dialogue completed!"
        }
      ]
    },

    // ===================================================================
    // READING: ALL MY PREFERENCES (Consolidation) 📚
    // ===================================================================
    {
      id: "m3-u10-reading",
      title: "📚 Final Reading: All My Preferences",
      category: "reading",
      context: "Martes - Presentación final. Alex lee su ensayo completo sobre sus preferencias a la clase. — Tuesday - Final presentation. Alex reads his complete essay about his preferences to the class.",
      questions: [
        {
          type: "passage",
          passage: "Hi! I'm Alex and I want to tell you about all my favorite things and what I don't like.\n\nLet's start with animals. My favorite animal is dogs because they're friendly and loyal. I like cats, but I like dogs more because dogs are more active. I don't like spiders at all because they're scary.\n\nMy favorite country is Italy because I love the food and the culture. I like Spain, but I prefer Italy because Italian food is more delicious. My least favorite country is Russia because it's too cold for me.\n\nI love all sports, but my favorite is tennis because it's fast and exciting. I like swimming, but I like tennis more because I can play it with my friends. I don't like golf very much because it's too slow.\n\nMy favorite type of music is rock because it's energetic. I also like jazz, but I like rock more because it makes me feel happy. I don't like classical music because it's boring for me.\n\nIn my family, my favorite person is my grandmother because she's very kind and warm. I like my uncle, but I like my grandmother more because she always listens to me. I don't like my cousin Tom very much because he's too serious.\n\nMy favorite place in my town is the park because it's beautiful and quiet. I like the cinema, but I like the park more because I can relax there. I don't like the bus station because it's dirty and noisy."
        },
        {
          type: "mc",
          q: "What is Alex's favorite animal?",
          options: ["Dogs", "Cats", "Parrots"],
          a: "Dogs",
          tip: "Check the animals paragraph!",
          successtip: "Dogs! Favorite identified!"
        },
        {
          type: "mc",
          q: "How many times does Alex use 'but I like X more'?",
          options: ["Six times", "Three times", "Once"],
          a: "Six times",
          tip: "Count all 'like more' structures!",
          successtip: "Six times! Structure counted!"
        },
        {
          type: "input",
          q: "Alex doesn't like spiders, Russia, golf, classical music, cousin Tom, and bus station. How many dislikes? ___",
          a: "six",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["6"] },
          tip: "Count all negative preferences!",
          successtip: "Six! All counted!"
        },
        {
          type: "order",
          q: "Why is tennis his favorite sport? Because it's ___",
          words: ["Because", "it's", "fast", "and", "exciting."],
          a: "Because it's fast and exciting.",
          tip: "Two reasons! Fast and exciting!",
          successtip: "Two reasons! Tennis!"
        },
        {
          type: "mc",
          q: "Which structure does Alex use most in this text?",
          options: ["I like X, but I like Y more because...", "My favorite is X", "I don't like X"],
          a: "I like X, but I like Y more because...",
          tip: "Count structure frequency!",
          successtip: "Like more! Most frequent!"
        },
        {
          type: "input",
          q: "Alex's least favorite country is ___ because it's too ___.",
          a: "Russia cold",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Country + reason!",
          successtip: "Russia cold! Found!"
        },
        {
          type: "mc",
          q: "Why does Alex like his grandmother more than his uncle?",
          options: ["She always listens to him", "She's funny", "She's young"],
          a: "She always listens to him",
          tip: "'Because she always listens to me'!",
          successtip: "She listens! Personal!"
        },
        {
          type: "input",
          q: "How many different topics does Alex cover? Animals, countries, sports, music, family, places = ___",
          a: "six",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["6"] },
          tip: "Count topics!",
          successtip: "Six topics! All modules!"
        },
        {
          type: "order",
          q: "Why doesn't Alex like the bus station?",
          words: ["Because", "it's", "dirty", "and", "noisy."],
          a: "Because it's dirty and noisy.",
          tip: "Two negative reasons!",
          successtip: "Two negatives! Place!"
        },
        {
          type: "mc",
          q: "Which preference level does Alex NOT use?",
          options: ["Can't stand / hate", "Like more", "Don't like very much"],
          a: "Can't stand / hate",
          tip: "Check vocabulary used!",
          successtip: "Doesn't use hate!"
        }
      ]
    },

    // ===================================================================
    // REVIEW: UNIT 10 CONSOLIDATION (10 questions)
    // ===================================================================
    {
      id: "m3-u10-review",
      title: "🔄 Unit 10 Review",
      category: "review",
      questions: [
        {
          type: "mc",
          q: "Which expresses TOP preference?",
          options: ["My favorite is tennis", "I like tennis more", "I like tennis"],
          a: "My favorite is tennis",
          tip: "Favorite = #1 choice!",
          successtip: "Favorite! Top choice!"
        },
        {
          type: "input",
          q: "Complete: 'I like cats, but I like dogs ___ because they're more active.'",
          a: "more",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Like + MORE!",
          successtip: "More! Simple preference!"
        },
        {
          type: "order",
          q: "Traduce: 'Mi animal favorito es el caballo porque son hermosos.'",
          words: ["My", "favorite", "animal", "is", "the", "horse", "because", "they're", "beautiful."],
          a: "My favorite animal is the horse because they're beautiful.",
          tip: "Complete structure! Favorite + because!",
          successtip: "Perfect translation!"
        },
        {
          type: "mc",
          q: "Mild negative preference:",
          options: ["I don't like it very much", "I hate it", "It's my least favorite"],
          a: "I don't like it very much",
          tip: "'Very much' makes it MILD!",
          successtip: "Mild! Not strong!"
        },
        {
          type: "input",
          q: "Complete: 'My favorite type of ___ is Italian food because it's delicious.'",
          a: "food",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'Type of' needs CATEGORY!",
          successtip: "Food! Category word!"
        },
        {
          type: "mc",
          q: "Best answer to 'Why is Spain your favorite country?'",
          options: ["Because the people are friendly and the food is great", "Spain", "Yes, I like Spain"],
          a: "Because the people are friendly and the food is great",
          tip: "'Why' needs 'because + reason'!",
          successtip: "Complete reason!"
        },
        {
          type: "input",
          q: "Complete: 'My favorite ___ is tennis, my favorite ___ is rock, and my favorite ___ is pizza.' (categories)",
          a: "sport music food",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Three categories!",
          successtip: "Three! All modules!"
        },
        {
          type: "order",
          q: "Negative: 'No me cae bien mi primo porque es aburrido.' →",
          words: ["I", "don't", "like", "my", "cousin", "because", "he's", "boring."],
          a: "I don't like my cousin because he's boring.",
          tip: "'No me cae bien' = don't like!",
          successtip: "Family dislike + reason!"
        },
        {
          type: "mc",
          q: "Which structure did you learn NEW in Unit 10?",
          options: ["Like more", "Love", "Hate"],
          a: "Like more",
          tip: "'Like more' = NEW!",
          successtip: "Like more! New structure!"
        },
        {
          type: "input",
          q: "How many topics did Unit 10 recycle? Animals, countries, house, town, family, sports, music, food = ___",
          a: "eight",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["8"] },
          tip: "Count all topics!",
          successtip: "Eight! Complete recycling!"
        }
      ]
    }

  ] // End of sets
};
